// ... imports unchanged
import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Card, CardContent } from "@mui/material";
import { useNavigate } from "react-router-dom";
import bannerVideo from "./banner.mp4";
import { ArrowForward } from "@mui/icons-material";
import axios from "axios";

const Banner = () => {
  const navigate = useNavigate();
  const [activeCount, setActiveCount] = useState(null);

  useEffect(() => {
    axios.get("https://app.stock101.org/count")
      .then(res => setActiveCount(res.data.count))
      .catch(err => console.error("Error fetching subscription count:", err));
  }, []);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: { xs: "auto", md: "50vh" },
        overflow: "hidden",
        pb: { xs: 4, md: 0 },
      }}
    >
      {/* Background Video */}
      <video
        autoPlay
        loop
        muted
        playsInline
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      >
        <source src={bannerVideo} type="video/mp4" />
      </video>

      {/* Dark overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
      />

      {/* Left-floating subscription card (desktop only) */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: { md: 40 },
          transform: "translateY(-50%)",
          display: { xs: "none", md: "block" },
          zIndex: 2,
        }}
      >
        <SubscriptionCard count={activeCount} />
      </Box>

      {/* Main content (centered) */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
          px: 2,
        }}
      >
        {/* Title */}
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: "40px", md: "80px" },
            fontWeight: "bold",
            fontFamily: "'Work Sans', sans-serif",
            letterSpacing: "2px",
            color: "#fff",
            animation: "typing 3s steps(30) 1s 1 normal both",
            whiteSpace: "nowrap",
            overflow: "hidden",
            display: "inline-block",
            borderRight: "4px solid #fff",
          }}
        >
          Stock101
        </Typography>

        {/* Subtitle */}
        <Typography
          variant="h6"
          sx={{
            fontSize: { xs: "16px", md: "20px" },
            fontWeight: 400,
            fontFamily: "'Work Sans', sans-serif",
            letterSpacing: "1px",
            animation: "fadeIn 2s ease-in-out 0.5s",
            mt: 2,
            color: "#fff",
          }}
        >
          FIND THE MOST PROFITABLE STOCK WITH EASE
        </Typography>

        {/* Buttons */}
        <Box
          sx={{
            mt: 4,
            display: "flex",
            justifyContent: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Button variant="outlined" onClick={() => navigate("/screener")} sx={buttonStyles}>
            NYSE Stock Screener <ArrowForward />
          </Button>
          <Button variant="outlined" onClick={() => navigate("/ranking_news")} sx={buttonStyles}>
            Ranking News <ArrowForward />
          </Button>
          <Button variant="outlined" onClick={() => navigate("/prize_target_news")} sx={buttonStyles}>
            Prize Target News <ArrowForward />
          </Button>
          <Button variant="outlined" onClick={() => navigate("/trending_news")} sx={buttonStyles}>
            Trending News <ArrowForward />
          </Button>
        </Box>

        {/* Mobile subscription card */}
        <Box sx={{ mt: 4, display: { xs: "block", md: "none" } }}>
          <SubscriptionCard count={activeCount} />
        </Box>
      </Box>

      {/* Animations */}
      <style>
        {`
          @keyframes typing {
            from { width: 0; }
            to { width: 100%; }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}
      </style>
    </Box>
  );
};

const SubscriptionCard = ({ count }) => (
  <Card
    sx={{
      minWidth: 200,
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      backdropFilter: "blur(10px)",
      borderRadius: 4,
      boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
      textAlign: "center",
      px: 3,
      py: 2,
      color: "#fff",
      animation: "fadeSlideUp 1s ease forwards",
      opacity: 0,
    }}
  >
    <CardContent>
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: 500,
          fontFamily: "'Work Sans', sans-serif",
          letterSpacing: 1,
          textTransform: "uppercase",
          color: "#90caf9",
        }}
      >
        Active Subscribers
      </Typography>
      <Typography
        variant="h3"
        sx={{
          fontWeight: 700,
          fontFamily: "'Work Sans', sans-serif",
          mt: 1,
          color: "#fff",
        }}
      >
        {count !== null ? count : '...'}
      </Typography>
      <Typography
        sx={{
          fontSize: "12px",
          mt: 1,
          fontFamily: "'Work Sans', sans-serif",
          color: "#eee",
        }}
      >
        Thank you for using <strong>Stock101</strong>
      </Typography>
    </CardContent>

    {/* Keyframe for animation */}
    <style>
      {`
        @keyframes fadeSlideUp {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}
    </style>
  </Card>
);

const buttonStyles = {
  fontSize: "14px",
  fontWeight: "bold",
  fontFamily: "'Work Sans', sans-serif",
  letterSpacing: "1px",
  padding: { xs: "8px 16px", md: "10px 20px" },
  color: "#fff",
  borderColor: "#fff",
  textTransform: "none",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    borderColor: "#fff",
  },
};

export default Banner;
